<template>
  <div>
    <NavBar />

    <!-- Hero Section -->
    <section class="hero" id="home">
      <div class="hero-image-full">
        <img v-for="(img, i) in weddingPhotos" :key="'w'+i" :src="img" alt="Wedding Photography" :class="{ active: i === weddingIndex }" :style="i === 2 ? { objectFit: 'cover', objectPosition: 'center top' } : i === 1 ? { objectFit: 'cover', objectPosition: 'center 20%' } : i === 4 ? { objectFit: 'cover', objectPosition: 'center 65%' } : i === 6 ? { objectFit: 'cover', objectPosition: 'center 25%' } : {}" />
      </div>
      <div class="hero-content">
        <h1>Your Day.<br><span class="italic">Exactly</span> As It Was.</h1>
        <p class="hero-subtitle">The moments between the moments.</p>
        <router-link to="/contact" class="hero-cta">Inquire Now</router-link>
      </div>
    </section>

    <!-- About Section -->
    <section class="about" id="about">
      <div class="monogram">EL</div>
      <h2>"We do not remember days, we remember moments."</h2>
      <p class="about-quote">— Cesare Pavese</p>
      <p>That's been true every single time I've shown up to a wedding.</p>
      <p>There's a version of your day that only exists for a few hours. The way he looked at you when you walked in before anyone else saw. That moment between songs when everyone was just... there. Your mom's face when she realized it was really happening.</p>
      <p>I'm there for all of it — quietly, in the background — making sure none of it disappears.</p>
      <a href="#services" class="about-cta">Book Your Experience</a>
    </section>

    <!-- Services Section -->
    <section class="services" id="services">
      <div class="service-block">
        <div class="service-image">
          <img v-for="(img, i) in weddingServicePhotos" :key="'ws'+i" :src="img" alt="Wedding Photography" :class="{ active: i === weddingServiceIndex }" />
        </div>
        <div class="service-content">
          <h3>Weddings</h3>
          <p>Your wedding day is going to be a blur — in the best way. I'm not going to slow it down or make you pose through it. I'm just going to be there, watching it all happen.</p>
          <p>And when it's over and you finally sit down and breathe, you'll have photos that remind you exactly how it felt.</p>
        </div>
      </div>

      <div class="service-block">
        <div class="service-image">
          <img v-for="(img, i) in couplesPhotos" :key="'cs'+i" :src="img" alt="Couples Photography" :class="{ active: i === couplesIndex }" />
        </div>
        <div class="service-content">
          <h3>Couples</h3>
          <p>I don't want you to "do a pose." I want you to just hang out with each other and let me follow you around. Tell me how you met. Show me your favorite spot. Be weird together.</p>
          <p>That's where the good photos are.</p>
        </div>
      </div>
    </section>

    <!-- Philosophy Section -->
    <section class="philosophy">
      <p class="philosophy-label">The Promise</p>
      <h2>I've been doing this long enough to know — the photos people actually treasure aren't the ones where everyone looks <span class="highlight">perfect.</span> They're the ones where everyone looks <span class="highlight">real.</span></h2>
      <a href="#about" class="philosophy-cta">The Experience</a>
    </section>

    <!-- Testimonial Section -->
    <section class="testimonial">
      <div class="reviews-header">
        <p class="reviews-label">Client Reviews</p>
        <div class="reviews-rating">
          <span class="stars">★★★★★</span>
          <span class="rating-text">5.0 on Google · 5 reviews</span>
        </div>
      </div>
      <div class="reviews-grid">
        <div class="review-card">
          <p class="review-text">"Evelina captured such beautiful moments of our family. Truly felt so easy around her. Loved the way the photos turned out. Highly recommend if you need a photoshoot."</p>
          <cite>— Anita Hanchar</cite>
        </div>
        <div class="review-card">
          <p class="review-text">"We wanted to capture our growing family during the holiday season and couldn't be more happy with how Evelina captured all the moments in such a short time. We felt at ease and even our son who is a bit shy warmed up to her. Definitely recommend her if you are looking for someone who is relatable, easy and good with kids!"</p>
          <cite>— Google Review</cite>
        </div>
        <div class="review-card">
          <p class="review-text">"We were impressed by the value of her services and final photographic output. She spent nearly an hour with our family which was more than she had initially intended and was quite pleasant throughout. Would highly recommend."</p>
          <cite>— Isabel Cote</cite>
        </div>
        <div class="review-card">
          <p class="review-text">"So sweet to have her help shoot our wedding! Love her style."</p>
          <cite>— Cayla Roberts</cite>
        </div>
      </div>
    </section>

    <!-- Portfolio CTA -->
    <section class="portfolio-cta" id="portfolio">
      <div class="portfolio-preview-grid">
        <div class="preview-img" v-for="(photo, i) in previewPhotos" :key="i">
          <img :src="photo" alt="Portfolio preview" />
        </div>
      </div>
      <div class="portfolio-cta-text">
        <h2><span class="italic">See</span> The Work</h2>
        <router-link to="/portfolio" class="portfolio-cta-button">View Portfolio</router-link>
      </div>
    </section>

    <!-- Contact Section -->
    <section class="contact" id="contact">
      <div class="contact-content">
        <h2>Let's Talk</h2>
        <p class="contact-subtitle">Tell me about your day, your family, your story. I want to know what matters to you.</p>
        <div class="contact-grid">
          <div class="contact-item">
            <h3>Email</h3>
            <a href="mailto:evialinaphotography@gmail.com">evialinaphotography@gmail.com</a>
          </div>
          <div class="contact-item">
            <h3>Phone</h3>
            <a href="tel:+18586342517">858-634-2517</a>
          </div>
          <div class="contact-item">
            <h3>Instagram</h3>
            <a href="https://www.instagram.com/evelinalodiphotography/" target="_blank">@evelinalodiphotography</a>
          </div>
        </div>
        <router-link to="/contact" class="contact-cta">Send an Inquiry</router-link>
      </div>
    </section>

    <FooterComponent />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onUnmounted } from "vue";
import NavBar from "@/components/nav.vue";
import FooterComponent from "@/components/FooterComponent.vue";

export default defineComponent({
  name: "HomeView",
  components: { NavBar, FooterComponent },
  setup() {
    const weddingPhotos = [
      require("@/assets/weddings/DSC09425 copy.jpg"),  // 0 - bride under veil
      require("@/assets/weddings/DSC09950 copy.jpg"),  // 1 - kissing under veil
      require("@/assets/weddings/DSC01092 copy.jpg"),  // 2 - Porsche
      require("@/assets/weddings/DSC01142 copy.jpg"),  // 3 - motion blur running
      require("@/assets/weddings/DSC02055 copy.jpg"),  // 4 - first kiss at cross
      require("@/assets/weddings/DSC00045 copy.jpg"),  // 5 - B&W lift/kiss
      require("@/assets/weddings/DSC00402 copy.jpg"),  // 6 - bride on stairs with veil
      require("@/assets/weddings/DSC00370 copy.jpg"),  // 7 - B&W cowboy groom
    ];

    const weddingServicePhotos = [
      require("@/assets/weddings/DSC00081 copy.jpg"),  // vineyard + mountains
      require("@/assets/weddings/DSC09408 copy.jpg"),  // bride at columns
      require("@/assets/weddings/DSC09587 copy.jpg"),  // Balboa Park couple
      require("@/assets/weddings/DSC02207 copy.jpg"),  // golden hour couple
      require("@/assets/weddings/DSC00032 copy.jpg"),  // couple at ornate building
    ];

    const weddingServiceIndex = ref(0);

    const couplesPhotos = [
      require("@/assets/couples/DSC04581 copy.jpg"),
      require("@/assets/couples/DSC04640 copy.jpg"),
      require("@/assets/couples/DSC05050 copy.jpg"),
      require("@/assets/couples/DSC05204 copy.jpg"),
      require("@/assets/couples/DSC05601 copy.jpg"),
      require("@/assets/couples/DSC05680 copy.jpg"),
      require("@/assets/couples/DSC05839 copy.jpg"),
      require("@/assets/couples/DSC06895.jpg"),
      require("@/assets/couples/DSC06984 copy.jpg"),
      require("@/assets/couples/DSC07045 copy.jpg"),
      require("@/assets/couples/DSC07215 copy.jpg"),
      require("@/assets/couples/DSC07258 copy.jpg"),
      require("@/assets/couples/DSC07630 copy.jpg"),
      require("@/assets/couples/DSC07901 copy.jpg"),
      require("@/assets/couples/DSC07916 copy.jpg"),
      require("@/assets/couples/DSC07941 copy.jpg"),
      require("@/assets/couples/DSC08131 copy.jpg"),
      require("@/assets/couples/DSC08314 copy.jpg"),
      require("@/assets/couples/DSC08603 copy.jpg"),
      require("@/assets/couples/DSC08666 copy.jpg"),
      require("@/assets/couples/DSC08714 copy.jpg"),
      require("@/assets/couples/DSC08729 copy.jpg"),
      require("@/assets/couples/DSC08811 copy.jpg"),
      require("@/assets/couples/DSC09117 copy.jpg"),
      require("@/assets/couples/DSC09173 copy.jpg"),
      require("@/assets/couples/DSC09199 copy.jpg"),
    ];

    const weddingIndex = ref(0);
    const couplesIndex = ref(0);
    let timer: ReturnType<typeof setInterval>;

    onMounted(() => {
      timer = setInterval(() => {
        weddingIndex.value = (weddingIndex.value + 1) % weddingPhotos.length;
        weddingServiceIndex.value = (weddingServiceIndex.value + 1) % weddingServicePhotos.length;
        couplesIndex.value = (couplesIndex.value + 1) % couplesPhotos.length;
      }, 3500);
    });

    onUnmounted(() => clearInterval(timer));

    const previewPhotos = [
      require("@/assets/weddings/DSC09425 copy.jpg"),   // bride under veil
      require("@/assets/couples/DSC08603 copy.jpg"),    // warm couples
      require("@/assets/family/DSC06049 copy.jpg"),    // family warm
      require("@/assets/weddings/DSC02055 copy.jpg"),   // first kiss
      require("@/assets/maternity/DSC02894-Enhanced-NR copy.jpg"), // maternity
    ];

    return { weddingPhotos, weddingServicePhotos, weddingServiceIndex, couplesPhotos, weddingIndex, couplesIndex, previewPhotos };
  },
});
</script>

<style lang="scss" scoped>
/* Hero Section - Full Width */
.hero {
  height: 100vh;
  padding-top: 76px;
  position: relative;
}

.hero-image-full {
  position: absolute;
  top: 76px; left: 0;
  width: 100%;
  height: calc(100% - 76px);
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background:
      linear-gradient(to top, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.15) 60%, rgba(0,0,0,0.1) 80%, transparent 100%),
      rgba(0,0,0,0.12);
    z-index: 1;
  }

  img {
    position: absolute;
    top: 0; left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0;
    transition: opacity 1.2s ease-in-out;

    &.active {
      opacity: 1;
    }
  }
}

.hero-content {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: white;
  z-index: 10;
  width: 90%;
  max-width: 90%;
  animation: fadeIn 1s ease-out;
}

.hero h1 {
  font-family: 'Playfair Display', serif;
  font-size: clamp(2.5rem, 5vw, 5rem);
  font-weight: 400;
  line-height: 1.1;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-shadow: 0 2px 40px rgba(0,0,0,0.4), 0 1px 8px rgba(0,0,0,0.25);
}

.hero h1 .italic {
  font-style: italic;
  font-weight: 400;
}

.hero-subtitle {
  font-size: clamp(0.9rem, 1.2vw, 1.1rem);
  letter-spacing: 0.3em;
  text-transform: uppercase;
  margin-bottom: 3rem;
  font-weight: 300;
  text-shadow: 0 1px 20px rgba(0,0,0,0.45);
}

.hero-cta {
  display: inline-block;
  padding: 1rem 2.5rem;
  background: transparent;
  color: white;
  text-decoration: none;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  font-size: 0.7rem;
  font-weight: 500;
  border: 1.5px solid white;
  transition: all 0.4s ease;

  &:hover {
    background: white;
    color: var(--charcoal);
  }
}

/* About Section */
.about {
  padding: 5rem 5%;
  text-align: center;
  background: white;
  animation: fadeIn 1s ease-out;
}

.monogram {
  width: 70px;
  height: 70px;
  margin: 0 auto 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Playfair Display', serif;
  font-size: 1.4rem;
  font-weight: 400;
  letter-spacing: 0.2em;
  color: var(--charcoal);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 1px solid var(--charcoal);
  }

  &::after {
    content: '';
    position: absolute;
    inset: 5px;
    border-radius: 50%;
    border: 0.5px solid rgba(42, 42, 42, 0.4);
  }
}

.about h2 {
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.8rem, 3vw, 2.6rem);
  font-weight: 400;
  line-height: 1.4;
  margin-bottom: 2rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.about p {
  font-size: clamp(1rem, 1.2vw, 1.15rem);
  line-height: 1.9;
  color: var(--grey);
  max-width: 800px;
  margin: 0 auto 2rem;
  font-weight: 300;
}

.about-quote {
  font-size: 0.85rem !important;
  letter-spacing: 0.1em;
  color: var(--grey) !important;
  margin-bottom: 2.5rem !important;
}

.about-cta {
  display: inline-block;
  margin-top: 2rem;
  padding: 0.8rem 0;
  color: var(--charcoal);
  text-decoration: none;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  font-size: 0.75rem;
  font-weight: 600;
  border-bottom: 2px solid var(--charcoal);
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.6;
  }
}

/* Services Section - Full Width Split */
.services {
  background: var(--cream);
  color: var(--charcoal);
}

.service-block {
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 600px;

  &:nth-child(even) {
    direction: rtl;

    > * {
      direction: ltr;
    }
  }

  &:nth-child(1) .service-image {
    background: linear-gradient(135deg, #C8BEB0 0%, #A89B8E 100%);
  }

  &:nth-child(2) .service-image {
    background: linear-gradient(135deg, #B5A89A 0%, #978A7C 100%);
  }

  & + .service-block {
    border-top: 1px solid var(--light-grey);
  }
}

.service-image {
  position: relative;
  overflow: hidden;

  img {
    position: absolute;
    top: 0; left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0;
    transition: opacity 1.2s ease-in-out;

    &.active {
      opacity: 1;
    }
  }
}

.service-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 8rem 8%;
  animation: fadeIn 1s ease-out;

  h3 {
    font-family: 'Playfair Display', serif;
    font-size: clamp(1.6rem, 2.5vw, 2.2rem);
    font-weight: 400;
    margin-bottom: 2rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  p {
    font-size: 1.05rem;
    line-height: 1.9;
    color: var(--grey);
    margin-bottom: 1.5rem;
    font-weight: 300;
  }
}

/* Philosophy Section */
.philosophy {
  background: white;
  padding: 5rem 5%;
  text-align: center;
  animation: fadeIn 1s ease-out;

  &::before {
    content: '';
    display: block;
    width: 100%;
    height: 1px;
    background: var(--light-grey);
    margin: 0 auto 4rem;
    max-width: 800px;
  }
}

.philosophy-label {
  font-size: 0.7rem;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  margin-bottom: 3rem;
  color: var(--grey);
  font-weight: 500;
}

.philosophy h2 {
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.6rem, 2.5vw, 2.4rem);
  font-weight: 400;
  line-height: 1.5;
  margin-bottom: 2rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.philosophy .highlight {
  text-transform: uppercase;
  font-weight: 400;
}

.philosophy-cta {
  display: inline-block;
  margin-top: 3rem;
  padding: 1rem 0;
  color: var(--charcoal);
  text-decoration: none;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  font-size: 0.75rem;
  font-weight: 600;
  border-bottom: 2px solid var(--charcoal);
}

/* Testimonial Section */
.testimonial {
  background: var(--cream);
  padding: 5rem 5%;
  color: var(--charcoal);
  animation: fadeIn 1s ease-out;
}

.reviews-header {
  text-align: center;
  margin-bottom: 3rem;
}

.reviews-label {
  font-size: 0.7rem;
  letter-spacing: 0.35em;
  text-transform: uppercase;
  font-weight: 500;
  color: var(--grey);
  margin-bottom: 1.2rem;
}

.reviews-rating {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.stars {
  color: #C8A96E;
  font-size: 1.2rem;
  letter-spacing: 0.1em;
}

.rating-text {
  font-size: 0.8rem;
  letter-spacing: 0.1em;
  color: var(--grey);
  font-weight: 300;
}

.reviews-grid {
  max-width: 1300px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2.5rem;
}

.review-card {
  border-top: 1px solid rgba(0,0,0,0.12);
  padding-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.review-text {
  font-family: 'Playfair Display', serif;
  font-size: 1rem;
  line-height: 1.8;
  font-style: italic;
  font-weight: 400;
  color: var(--charcoal);
  flex: 1;
}

.review-card cite {
  font-family: 'Libre Franklin', sans-serif;
  font-size: 0.7rem;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  font-style: normal;
  font-weight: 500;
  color: var(--grey);
}

@media (max-width: 1024px) {
  .reviews-grid {
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }
}

@media (max-width: 600px) {
  .reviews-grid {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
}

/* Portfolio CTA */
.portfolio-cta {
  background: white;
  padding: 5rem 5%;
  text-align: center;
  animation: fadeIn 1s ease-out;
}

.portfolio-preview-grid {
  max-width: 1400px;
  margin: 0 auto 3.5rem;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.5rem;
  height: 340px;
}

.preview-img {
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center 30%;
    transition: transform 0.6s ease;
    display: block;

    &:hover {
      transform: scale(1.05);
    }
  }
}

.portfolio-cta-text {
  h2 {
    font-family: 'Playfair Display', serif;
    font-size: clamp(1.8rem, 3vw, 2.8rem);
    font-weight: 400;
    margin-bottom: 0;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--charcoal);

    .italic {
      font-style: italic;
      text-transform: none;
    }
  }
}

@media (max-width: 768px) {
  .portfolio-preview-grid {
    grid-template-columns: repeat(3, 1fr);
    height: 220px;
  }
}

.portfolio-cta-button {
  display: inline-block;
  margin-top: 2rem;
  padding: 0.8rem 0;
  color: var(--charcoal);
  text-decoration: none;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  font-size: 0.75rem;
  font-weight: 600;
  border-bottom: 2px solid var(--charcoal);
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.5;
  }
}

/* Contact Section */
.contact {
  background: var(--cream);
  padding: 6rem 5%;
  position: relative;
}

.contact-content {
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;

  h2 {
    font-family: 'Playfair Display', serif;
    font-size: clamp(2rem, 3.5vw, 3rem);
    font-weight: 400;
    margin-bottom: 1rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--charcoal);
  }
}

.contact-subtitle {
  font-size: 1rem;
  letter-spacing: 0.03em;
  margin-bottom: 4rem;
  color: var(--grey);
  font-weight: 300;
}

.contact-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3rem;
  margin-bottom: 4rem;
}

.contact-item {
  h3 {
    font-size: 0.65rem;
    margin-bottom: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--grey);
  }

  a {
    color: var(--charcoal);
    text-decoration: none;
    font-size: 0.95rem;
    transition: opacity 0.3s ease;
    font-weight: 300;

    &:hover {
      opacity: 0.6;
    }
  }
}

.contact-cta {
  display: inline-block;
  padding: 1rem 3rem;
  background: var(--charcoal);
  color: var(--cream);
  text-decoration: none;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 50px;
  transition: all 0.3s ease;

  &:hover {
    background: var(--grey);
    transform: translateY(-2px);
  }
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Responsive */
@media (max-width: 1024px) {
  .contact-grid {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }

  .service-block {
    grid-template-columns: 1fr;
  }

  .service-image {
    aspect-ratio: 4 / 3;
  }
}

@media (max-width: 768px) {
  .service-content {
    padding: 4rem 5%;
  }
}
</style>
